import { useState, useEffect, useCallback } from 'react';
import { notificationService, NotificationPayload } from '../services/notificationService';

export interface UseNotificationsReturn {
  isSupported: boolean;
  isPermissionGranted: boolean;
  token: string | null;
  isInitialized: boolean;
  initialize: () => Promise<boolean>;
  showNotification: (payload: NotificationPayload) => void;
  sendTokenToServer: () => Promise<boolean>;
}

// Module-level flag to prevent duplicate initialization across hook instances
let hasAutoInitialized = false;

export const useNotifications = (): UseNotificationsReturn => {
  const [isSupported, setIsSupported] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize notifications
  const initialize = useCallback(async (): Promise<boolean> => {
    try {
      const success = await notificationService.initialize();
      if (success) {
        setToken(notificationService.getToken());
        setIsPermissionGranted(notificationService.isPermissionGranted());
        setIsInitialized(true);
        
        // Start listening for foreground messages
        notificationService.startListening();
      }
      return success;
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      return false;
    }
  }, []);

  // Show notification
  const showNotification = useCallback((payload: NotificationPayload) => {
    notificationService.showNotification(payload);
  }, []);

  // Send token to server
  const sendTokenToServer = useCallback(async (): Promise<boolean> => {
    if (token) {
      return await notificationService.sendTokenToServer(token);
    }
    return false;
  }, [token]);

  // Initialize on mount
  useEffect(() => {
    setIsSupported(notificationService.isNotificationSupported());
    setIsPermissionGranted(notificationService.isPermissionGranted());
    
    // Auto-initialize if permission is already granted (only once, even in React Strict Mode)
    if (notificationService.isPermissionGranted() && !hasAutoInitialized) {
      hasAutoInitialized = true;
      initialize();
    }
  }, [initialize]);

  return {
    isSupported,
    isPermissionGranted,
    token,
    isInitialized,
    initialize,
    showNotification,
    sendTokenToServer
  };
};

export default useNotifications;
